import { Add, FileDownload, FileUpload } from "@mui/icons-material";
import { Alert, Button, Stack } from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";

import {
  isSecretKey,
  MAX_ENV_FILE_BYTES,
  MAX_ENV_VAR_COUNT,
  parseEnvFile,
  toEnvFile,
} from "../utils";
import type { EnvironmentVar } from "./EnvironmentVarsFieldRow";
import EnvironmentVarsFieldRow from "./EnvironmentVarsFieldRow";
import { AddRowButton } from "./NewJobFormLayout";

// Don't show secret warning for now, because people use secrets in env vars.
// But eventually we want to move away from that and then we can show warning.
const ENABLE_SECRET_WARNING = false;

type Props = {
  values: string[];
  onUpdate: (values: string[]) => void;
};

function parseVar(raw: string): EnvironmentVar {
  const equalsIndex = raw.indexOf("=");
  return {
    name: raw.slice(0, equalsIndex),
    value: raw.slice(equalsIndex + 1),
  };
}

function stringifyVars(vars: EnvironmentVar[]) {
  return vars.map(({ name, value }) => `${name}=${value}`);
}

let nextRowKey = 0;
function getUniqueRowKey() {
  return nextRowKey++;
}

const EnvironmentVarsField = React.memo(function EnvironmentVarsField({
  values,
  onUpdate,
}: Props) {
  const vars = useMemo(() => values.map(parseVar), [values]);

  const [rowKeys, setRowKeys] = useState(() => vars.map(getUniqueRowKey));
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [overwrittenKeys, setOverwrittenKeys] = useState<string[]>([]);

  const hasSecrets = useMemo(
    () => ENABLE_SECRET_WARNING && vars.some((v) => isSecretKey(v.name)),
    [vars],
  );

  const updateVar = useCallback(
    (index: number, patch: Partial<EnvironmentVar>) => {
      onUpdate(
        stringifyVars(
          vars.map((v, i) => (i === index ? { ...v, ...patch } : v)),
        ),
      );
    },
    [vars, onUpdate],
  );

  const handleAdd = useCallback(() => {
    setRowKeys((keys) => keys.concat(getUniqueRowKey()));
    onUpdate(stringifyVars(vars.concat({ name: "", value: "" })));
  }, [vars, onUpdate]);

  const handleClear = useCallback(
    (index: number) => {
      setRowKeys((keys) => keys.filter((_, i) => i !== index));
      onUpdate(stringifyVars(vars.filter((_, i) => i !== index)));
    },
    [vars, onUpdate],
  );

  const handleUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";

      if (file.size > MAX_ENV_FILE_BYTES) {
        setUploadError(
          `File exceeds the ${MAX_ENV_FILE_BYTES / 1024} KB size limit`,
        );
        setOverwrittenKeys([]);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        const fileVars: Record<string, string> = {};
        try {
          for (const [k, v] of Object.entries(parseEnvFile(content))) {
            fileVars[k] = v;
          }
        } catch (err) {
          setUploadError(err instanceof Error ? err.message : String(err));
          setOverwrittenKeys([]);
          return;
        }

        // If every existing row is blank (e.g. the default empty rows on a
        // fresh form), trim them all so the upload starts clean. If any row has
        // a name or value, leave all rows as-is and merge the file into them.
        const allEmpty = vars.every((v) => v.name === "" && v.value === "");
        const keptVars = allEmpty ? [] : vars;

        const existingKeys = new Set(keptVars.map((v) => v.name));
        const newKeys = Object.keys(fileVars).filter(
          (k) => !existingKeys.has(k),
        );
        if (keptVars.length + newKeys.length > MAX_ENV_VAR_COUNT) {
          setUploadError(
            `Merging would exceed the ${MAX_ENV_VAR_COUNT} variable limit`,
          );
          setOverwrittenKeys([]);
          return;
        }

        // Existing keys the file replaces with a *different* value.
        const overwritten = keptVars
          .filter((v) => v.name in fileVars && fileVars[v.name] !== v.value)
          .map((v) => v.name);

        setUploadError(null);
        setOverwrittenKeys([...new Set(overwritten)]);
        // Keep row keys aligned: drop all keys if we trimmed the all-empty
        // rows, then append a fresh key per newly added var.
        setRowKeys((keys) => [
          ...(allEmpty ? [] : keys),
          ...newKeys.map(() => getUniqueRowKey()),
        ]);
        onUpdate([
          ...keptVars.map(({ name, value }) =>
            name in fileVars ? `${name}=${fileVars[name]}` : `${name}=${value}`,
          ),
          ...newKeys.map((k) => `${k}=${fileVars[k]}`),
        ]);
      };
      reader.readAsText(file);
    },
    [vars, onUpdate],
  );

  const handleDownload = useCallback(() => {
    let content: string;
    try {
      content = toEnvFile(values);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
      return;
    }
    setUploadError(null);
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = ".env";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [values]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Stack spacing={3}>
      {hasSecrets && (
        <Alert severity="warning">Possible secrets detected.</Alert>
      )}
      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}
      {overwrittenKeys.length > 0 && (
        <Alert severity="warning" onClose={() => setOverwrittenKeys([])}>
          Upload overwrote {overwrittenKeys.length} existing variable
          {overwrittenKeys.length === 1 ? "" : "s"} (
          {overwrittenKeys.join(", ")}).
        </Alert>
      )}
      {vars.map(({ name, value }, index) => (
        <EnvironmentVarsFieldRow
          key={rowKeys[index]}
          rowKey={rowKeys[index]}
          name={name}
          value={value}
          allNames={vars.map((v) => v.name)}
          onUpdate={(patch) => updateVar(index, patch)}
          onClear={() => handleClear(index)}
        />
      ))}
      <AddRowButton color="primary" onClick={handleAdd}>
        <Add /> Add environment variable
      </AddRowButton>
      <Stack direction="row" spacing={1}>
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={<FileUpload />}
        >
          Upload .env
          <input
            ref={fileInputRef}
            type="file"
            hidden
            // No `accept` filter: `.env` files have no standard MIME type, so
            // OS file pickers grey them out inconsistently (some `.env` files
            // selectable, others not). Content is validated on parse instead.
            onChange={handleUpload}
          />
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownload />}
          onClick={handleDownload}
          disabled={vars.length === 0}
        >
          Download .env
        </Button>
      </Stack>
    </Stack>
  );
});

export default EnvironmentVarsField;
